import type { FormField } from '@lucid/lego';

type QuoteFormDefaultValues = {
  prospectType?: string;
  preferredPaymentMethod?: string;
  deliveryTimeline?: string;
  companyName?: string;
  companyTaxIdentification: string;
  invoiceAddressLine1: string;
  invoiceCity: string;
  invoicePostalCode: string;
};

type QuoteFormOptions = {
  /** Current status of the quote - form is disabled when not 'Active' */
  currentStatus?: string;
  /** Docusign status - form is disabled when 'Shared', 'Sent', or 'DocuSign Sent' */
  docusignStatus?: string;
};

/**
 * Checks if the form should be disabled based on current status or docusign status
 * @param currentStatus - The current status of the quote
 * @param docusignStatus - The docusign status of the quote
 * @returns true if form should be disabled
 */
const isFormDisabled = (currentStatus?: string, docusignStatus?: string): boolean => {
  // Disable if docusign has been sent
  if (docusignStatus) {
    const status = docusignStatus.toLowerCase();
    if (status === 'shared' || status === 'sent' || status === 'docusign sent') {
      return true;
    }
  }
  // If no currentStatus provided, form is enabled (for new quotes)
  if (!currentStatus) return false;
  // Disable form when status is not Active
  return currentStatus !== 'Active';
};

/**
 * Form fields configuration for the Create New Quote form
 * @param defaultValues - Optional default values for form fields
 * @param options - Optional configuration options including currentStatus and docusignStatus
 * @returns Array of form field configurations
 */
export const createQuoteFormFields = (
  defaultValues?: QuoteFormDefaultValues,
  options?: QuoteFormOptions
): FormField[] => {
  const disabled = isFormDisabled(options?.currentStatus, options?.docusignStatus);

  return [
    {
      id: 'paymentGroup',
      fieldType: 'group',
      title: 'Payment',
      width: 'full',
      variant: 'flat',
      children: [
        {
          id: 'prospectType',
          fieldType: 'dropdown',
          name: 'prospectType',
          apiKey: 'prospectType',
          label: 'Customer Type',
          placeholder: 'Select customer type',
          required: true,
          width: 'half',
          defaultValue: defaultValues?.prospectType,
          disabled,
          options: [
            { label: 'Private', value: 'Private' },
            { label: 'Business', value: 'Business' },
          ],
          validationMessages: {
            required: 'Customer type is required',
          },
        },
        {
          id: 'preferredPaymentMethod',
          fieldType: 'dropdown',
          name: 'preferredPaymentMethod',
          apiKey: 'preferredPaymentMethod',
          label: 'Preferred Payment method',
          placeholder: 'Select payment method',
          required: true,
          width: 'half',
          defaultValue: defaultValues?.preferredPaymentMethod,
          disabled,
          options: [
            { label: 'Cash', value: 'Cash' },
            { label: 'LFS Loan', value: 'LFS Loan' },
            { label: '3rd Party Finance', value: '3rd Party Financing' },
          ],
          validationMessages: {
            required: 'Payment method is required',
          },
        },
        {
          id: 'deliveryTimeline',
          fieldType: 'date_picker',
          name: 'deliveryTimeline',
          apiKey: 'deliveryTimeline',
          label: 'Delivery Timeline',
          required: true,
          width: 'full',
          size: 'medium',
          minDate: 'today',
          defaultValue: defaultValues?.deliveryTimeline,
          disabled,
          validationMessages: {
            required: 'Delivery timeline is required',
          },
        },
      ],
    },
    {
      id: 'businessInformationGroup',
      fieldType: 'group',
      title: 'Business Information',
      width: 'full',
      dependency: {
        name: 'prospectType',
        value: 'Business',
      },
      children: [
        {
          id: 'companyName',
          fieldType: 'text',
          name: 'companyName',
          apiKey: 'companyName',
          label: 'Company Name',
          placeholder: 'Enter Company Name',
          required: true,
          width: 'half',
          defaultValue: defaultValues?.companyName,
          disabled,
          validationMessages: {
            required: 'Company name is required',
          },
        },
        {
          id: 'companyTaxId',
          fieldType: 'text',
          name: 'companyTaxId',
          apiKey: 'companyTaxId',
          label: 'Company Tax ID',
          placeholder: 'Enter Company Tax ID',
          defaultValue: defaultValues?.companyTaxIdentification,
          required: true,
          width: 'half',
          disabled,
          validationMessages: {
            required: 'Company Tax ID is required',
          },
        },
        {
          id: 'invoiceAddress',
          fieldType: 'text',
          name: 'invoiceAddress',
          apiKey: 'invoiceAddress',
          label: 'Invoice Address',
          placeholder: 'Enter Invoice Address',
          required: true,
          width: 'full',
          defaultValue: defaultValues?.invoiceAddressLine1,
          disabled,
          validationMessages: {
            required: 'Invoice address is required',
          },
        },
        {
          id: 'businessCity',
          fieldType: 'text',
          name: 'businessCity',
          apiKey: 'businessCity',
          label: 'City',
          placeholder: 'Enter City',
          required: true,
          width: 'half',
          defaultValue: defaultValues?.invoiceCity,
          disabled,
          validationMessages: {
            required: 'City is required',
          },
        },
        {
          id: 'businessPostalCode',
          fieldType: 'text',
          name: 'businessPostalCode',
          apiKey: 'businessPostalCode',
          label: 'Postal Code',
          placeholder: 'Enter Postal Code',
          required: true,
          width: 'half',
          defaultValue: defaultValues?.invoicePostalCode,
          disabled,
          validationMessages: {
            required: 'Postal code is required',
          },
        },
      ],
    },
    {
      id: 'financeProviderGroup',
      fieldType: 'group',
      title: 'Finance Provider Details',
      width: 'full',
      dependency: {
        name: 'preferredPaymentMethod',
        value: '3rd Party Financing',
      },
      children: [
        {
          id: 'financeCompanyName',
          fieldType: 'text',
          name: 'financeCompanyName',
          apiKey: 'financeCompanyName',
          label: 'Company Name',
          placeholder: 'Enter Company Name',
          required: true,
          width: 'full',
          disabled,
          validationMessages: {
            required: 'Finance company name is required',
          },
        },
        {
          id: 'financeCompanyAddress',
          fieldType: 'text',
          name: 'financeCompanyAddress',
          apiKey: 'financeCompanyAddress',
          label: 'Company Address',
          placeholder: 'Enter Company Address',
          required: true,
          width: 'full',
          disabled,
          validationMessages: {
            required: 'Company address is required',
          },
        },
        {
          id: 'financeCity',
          fieldType: 'text',
          name: 'financeCity',
          apiKey: 'financeCity',
          label: 'City',
          placeholder: 'Enter City',
          required: true,
          width: 'half',
          disabled,
          validationMessages: {
            required: 'City is required',
          },
        },
        {
          id: 'financePostalCode',
          fieldType: 'text',
          name: 'financePostalCode',
          apiKey: 'financePostalCode',
          label: 'Postal Code',
          placeholder: 'Enter Postal Code',
          required: true,
          width: 'half',
          disabled,
          validationMessages: {
            required: 'Postal code is required',
          },
        },
        {
          id: 'financeVat',
          fieldType: 'text',
          name: 'financeVat',
          apiKey: 'financeVat',
          label: 'VAT#',
          placeholder: 'Enter VAT#',
          required: false,
          width: 'full',
          disabled,
        },
      ],
    },
    {
      id: 'quotationDetailsGroup',
      fieldType: 'group',
      title: 'Quotation Details',
      width: 'full',
      dependency: {
        name: 'preferredPaymentMethod',
        value: 'LFS Loan',
      },
      children: [
        {
          id: 'uploadBankQuotation',
          fieldType: 'text',
          type: 'file',
          name: 'uploadBankQuotation',
          apiKey: 'uploadBankQuotation',
          label: 'Upload Bank Quotation',
          placeholder: 'Upload Bank Quotation',
          required: false,
          width: 'full',
          disabled,
        },
        {
          id: 'quoteId',
          fieldType: 'text',
          name: 'quoteId',
          apiKey: 'quoteId',
          label: 'Quote ID',
          placeholder: 'Enter Quote ID',
          required: true,
          width: 'full',
          disabled,
          validationMessages: {
            required: 'Quote ID is required',
          },
        },
        {
          id: 'agentSubventionAmount',
          fieldType: 'text',
          name: 'agentSubventionAmount',
          apiKey: 'agentSubventionAmount',
          label: 'Agent Subvention Amount (€)',
          placeholder: 'Enter amount',
          required: false,
          width: 'full',
          disabled,
        },
        {
          id: 'subventionNote',
          fieldType: 'content',
          content: 'Discounts beyond xxx would be deducted from your compensation',
          variant: 'body',
          size: 'small',
          color: 'warning-1',
        },
        {
          id: 'monthlyPaymentInclVat',
          fieldType: 'text',
          name: 'monthlyPaymentInclVat',
          apiKey: 'monthlyPaymentInclVat',
          label: 'Monthly Payment Incl. VAT (€)',
          placeholder: 'Enter monthly payment',
          required: true,
          width: 'full',
          disabled,
          validationMessages: {
            required: 'Monthly payment is required',
          },
        },
        {
          id: 'downpayment',
          fieldType: 'text',
          name: 'downpayment',
          apiKey: 'downpayment',
          label: 'Downpayment (€)',
          placeholder: 'Enter downpayment',
          required: false,
          width: 'full',
          disabled,
        },
      ],
    },
    {
      id: 'incentivesGroup',
      fieldType: 'group',
      title: 'Incentives',
      width: 'full',
      children: [
        {
          id: 'requestedIncentives',
          fieldType: 'checkbox_group',
          name: 'requestedIncentives',
          apiKey: 'requestedIncentives',
          label: 'Requested Incentives',
          width: 'full',
          layout: 'vertical',
          required: true,
          disabled,
          options: [
            { value: 'charging_voucher', label: 'Charging Voucher' },
            { value: 'trade_in_support', label: 'Trade-in Support' },
          ],
          validationMessages: {
            required: 'Requested incentives is required',
          },
        },
        {
          id: 'cashDiscount',
          fieldType: 'number',
          name: 'cashDiscount',
          apiKey: 'cashDiscount',
          label: 'Cash Discount (€)',
          placeholder: 'Enter amount',
          required: false,
          width: 'half',
          disabled,
          pattern: /^[0-9]+$/,
          validationMessages: {
            pattern: 'Please enter whole numbers only',
          },
        },
        {
          id: 'percentageDiscount',
          fieldType: 'text',
          name: 'percentageDiscount',
          apiKey: 'percentageDiscount',
          label: 'Percentage Discount (%)',
          placeholder: 'Enter Percentage',
          required: false,
          width: 'half',
          disabled,
          disabledDependency: {
            name: 'cashDiscount',
          },
        },
        {
          id: 'discountNote',
          fieldType: 'content',
          content: 'Discounts beyond xxx would be deducted from your compensation.',
          variant: 'body',
          size: 'small',
          color: 'warning-1',
        },
      ],
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      align: 'spaceBetween',
      buttons: [
        {
          id: 'submitButton',
          label: 'Save Quote',
          type: 'primary',
          size: 'medium',
          color: 'neutral',
          theme: 'dark',
          isFluid: true,
          disabled,
        },
      ],
    },
  ];
};
